import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import { Box, Button, Dialog, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useParams } from "react-router-dom";
import { MapPin, Calendar } from "lucide-react";
import sh from "bundles/common/utils/sh";

const projectTypeLabel = (intl, type) =>
  type
    ? intl.formatMessage({ id: `candidate.profile_tab.project_type.${type}`, defaultMessage: type })
    : null;

const formatEndDate = (locale, endDate) => {
  if (!endDate) return null;
  const date = new Date(endDate);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(locale === "fr-fr" ? "fr" : locale, {
    month: "long",
    year: "numeric",
  }).format(date);
};

// Green-underlined section title — matches ProjectPage / company view idiom.
const SectionHeader = ({ children }) => (
  <Typography
    sx={{
      fontSize: "1.25rem",
      color: "hsl(222.2, 84%, 4.9%)",
      letterSpacing: "-0.01em",
      fontFamily: "Segoe UI Bold, system-ui, sans-serif",
      display: "inline-block",
      borderBottom: "2px solid hsl(152, 60%, 32%)",
      pb: "0.25rem",
    }}
  >
    {children}
  </Typography>
);

// Neutral pill used for skills; the green variant flags the project type.
const Pill = ({ children, accent }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      px: "0.75rem",
      py: "0.375rem",
      borderRadius: "9999px",
      fontSize: "0.8125rem",
      fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
      lineHeight: 1,
      backgroundColor: accent ? "hsla(153, 68%, 25%, 0.08)" : "hsl(210, 40%, 96.1%)",
      color: accent ? "hsl(153, 68%, 25%)" : "hsl(222.2, 84%, 4.9%)",
    }}
  >
    {children}
  </Box>
);

// Small icon + muted text row (location, date).
const MetaItem = ({ icon: Icon, children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "0.375rem", color: "hsl(215.4, 16.3%, 46.9%)" }}>
    <Icon size={15} />
    <Typography sx={{ fontSize: "0.875rem", color: "inherit" }}>{children}</Typography>
  </Box>
);

const PublicProjects = () => {
  const intl = useIntl();
  const theme = useTheme();
  const params = useParams();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const authenticityToken = useSelector((state) => state.authenticity_token);

  const [candidate, setCandidate] = useState(null);
  const [projects, setProjects] = useState([]);
  const [recruiterSignupUrl, setRecruiterSignupUrl] = useState(null);
  const [lightboxUrl, setLightboxUrl] = useState(null);

  useEffect(() => {
    sh.get(`/public_projects/${params.token}`, {
      authenticity_token: authenticityToken,
    })
      .then((res) => {
        if (res?.data?.candidate) {
          setCandidate(res.data.candidate);
          setProjects(res.data.projects || []);
          setRecruiterSignupUrl(res.data.recruiter_signup_url);
        } else {
          // Stale / disabled / invalid link: send visitors to the landing page.
          window.location.href = `/${i18nLocale}`;
        }
      })
      .catch(() => {
        window.location.href = `/${i18nLocale}`;
      });
  }, [params.token]);

  if (!candidate) return null;

  const allSkills = [
    ...(candidate.hard_skills || []),
    ...(candidate.soft_skills || []),
    ...(candidate.language_skills || []),
  ];

  const goToSignup = () => {
    if (recruiterSignupUrl) window.location.href = recruiterSignupUrl;
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "hsl(0, 0%, 100%)", pb: "4rem" }}>
      <Box
        sx={{
          mx: "auto",
          maxWidth: "72rem",
          px: { xs: "1rem", md: "2rem" },
          pt: { xs: "1.75rem", md: "2.5rem" },
        }}
      >
        {/* Candidate header */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "center", sm: "flex-start" },
            textAlign: { xs: "center", sm: "left" },
            gap: { xs: "1rem", sm: "1.5rem" },
          }}
        >
          <Box
            component="img"
            src={candidate.profile_picture_url}
            alt={candidate.full_name}
            sx={{
              flexShrink: 0,
              width: { xs: 104, md: 120 },
              height: { xs: 104, md: 120 },
              borderRadius: "9999px",
              objectFit: "cover",
              border: "4px solid hsl(0, 0%, 100%)",
              boxShadow:
                "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)",
              backgroundColor: "hsl(210, 40%, 96.1%)",
            }}
          />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              component="h1"
              sx={{
                fontSize: { xs: "1.5rem", md: "1.875rem" },
                color: "hsl(222.2, 84%, 4.9%)",
                letterSpacing: "-0.025em",
                lineHeight: 1.2,
                fontFamily: "Segoe UI Bold, system-ui, sans-serif",
              }}
            >
              {candidate.full_name}
            </Typography>
            {candidate.job_title && (
              <Typography
                sx={{
                  mt: "0.375rem",
                  fontSize: "1rem",
                  color: "hsl(215.4, 16.3%, 46.9%)",
                  fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                }}
              >
                {candidate.job_title}
              </Typography>
            )}
            {candidate.location && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: { xs: "center", sm: "flex-start" },
                  gap: "0.375rem",
                  mt: "0.5rem",
                  color: "hsl(215.4, 16.3%, 46.9%)",
                }}
              >
                <MapPin size={16} />
                <Typography sx={{ fontSize: "0.875rem", color: "inherit" }}>
                  {candidate.location}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {candidate.presentation && (
          <Typography
            sx={{
              mt: "1.75rem",
              color: "hsl(215.4, 16.3%, 46.9%)",
              fontSize: "0.9375rem",
              lineHeight: 1.85,
              whiteSpace: "pre-line",
            }}
          >
            {candidate.presentation}
          </Typography>
        )}

        {allSkills.length > 0 && (
          <Box sx={{ mt: "1.5rem", display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
            {allSkills.map((skill) => (
              <Pill key={skill.resource_skill_id || skill.name}>{skill.name}</Pill>
            ))}
          </Box>
        )}

        {/* Projects */}
        <Box sx={{ mt: "2.5rem" }}>
          <SectionHeader>
            <FormattedMessage id="portfolio.projects_heading" />
          </SectionHeader>
        </Box>

        {projects.length === 0 ? (
          <Typography
            sx={{ mt: "1.5rem", fontSize: "0.875rem", color: "hsl(215.4, 16.3%, 46.9%)" }}
          >
            <FormattedMessage id="portfolio.no_projects" />
          </Typography>
        ) : (
          <Box sx={{ mt: "1.75rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
            {projects.map((project) => {
              const typeLabel = projectTypeLabel(intl, project.project_type);
              const endLabel = formatEndDate(intl.locale, project.end_date);
              return (
                <Box
                  key={project.id}
                  sx={{
                    border: "1px solid hsl(214.3, 31.8%, 91.4%)",
                    borderRadius: "0.75rem",
                    p: { xs: "1.25rem", md: "1.5rem" },
                    backgroundColor: "hsl(0, 0%, 100%)",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "1.125rem",
                      color: "hsl(222.2, 84%, 4.9%)",
                      letterSpacing: "-0.01em",
                      lineHeight: 1.3,
                      fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                    }}
                  >
                    {project.title}
                  </Typography>

                  {(typeLabel || project.location || endLabel) && (
                    <Box
                      sx={{
                        mt: "0.75rem",
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: "0.75rem",
                      }}
                    >
                      {typeLabel && <Pill accent>{typeLabel}</Pill>}
                      {project.location && <MetaItem icon={MapPin}>{project.location}</MetaItem>}
                      {endLabel && <MetaItem icon={Calendar}>{endLabel}</MetaItem>}
                    </Box>
                  )}

                  {project.description && (
                    <Typography
                      sx={{
                        mt: "1rem",
                        color: "hsl(215.4, 16.3%, 46.9%)",
                        fontSize: "0.9375rem",
                        lineHeight: 1.85,
                        whiteSpace: "pre-line",
                      }}
                    >
                      {project.description}
                    </Typography>
                  )}

                  {project.photo_urls && project.photo_urls.length > 0 && (
                    <Box
                      sx={{
                        mt: "1.25rem",
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "repeat(2, 1fr)",
                          sm: "repeat(3, 1fr)",
                          md: "repeat(4, 1fr)",
                        },
                        gap: "0.625rem",
                      }}
                    >
                      {project.photo_urls.map((url) => (
                        <Box
                          key={url}
                          component="img"
                          src={url}
                          alt={project.title}
                          loading="lazy"
                          onClick={() => setLightboxUrl(url)}
                          sx={{
                            width: "100%",
                            aspectRatio: "1 / 1",
                            objectFit: "cover",
                            borderRadius: "0.5rem",
                            cursor: "pointer",
                            transition: "opacity 0.15s",
                            "&:hover": { opacity: 0.9 },
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}

        {/* Closing CTA */}
        <Box
          sx={{
            mt: "3rem",
            borderRadius: "0.75rem",
            backgroundColor: "hsl(153, 68%, 25%)",
            px: { xs: "1.5rem", md: "2rem" },
            py: { xs: "2rem", md: "2.5rem" },
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "1.25rem", md: "1.5rem" },
              color: "hsl(0, 0%, 100%)",
              letterSpacing: "-0.01em",
              fontFamily: "Segoe UI Bold, system-ui, sans-serif",
            }}
          >
            <FormattedMessage id="portfolio.footer_cta.title" values={{ name: candidate.first_name }} />
          </Typography>
          <Button
            onClick={goToSignup}
            disableRipple
            sx={{
              mt: "1.25rem",
              px: "1.75rem",
              height: "auto",
              minHeight: 0,
              py: "0.625rem",
              borderRadius: "9999px",
              fontSize: "0.9375rem",
              textTransform: "none",
              fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
              backgroundColor: "hsl(0, 0%, 100%)",
              color: "hsl(153, 68%, 25%)",
              transition: "background-color 0.15s",
              "&:hover": { backgroundColor: "hsla(0, 0%, 100%, 0.88)" },
            }}
          >
            <FormattedMessage id="portfolio.footer_cta.button" />
          </Button>
        </Box>
      </Box>

      <Dialog
        open={Boolean(lightboxUrl)}
        onClose={() => setLightboxUrl(null)}
        maxWidth="lg"
        fullScreen={isMobile}
      >
        {lightboxUrl && (
          <Box
            component="img"
            src={lightboxUrl}
            alt=""
            sx={{ maxWidth: "100%", maxHeight: "90vh", objectFit: "contain", display: "block" }}
            onClick={() => setLightboxUrl(null)}
          />
        )}
      </Dialog>
    </Box>
  );
};

export default PublicProjects;
